import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2, ChevronDown, ChevronRight, Search, GripVertical, FolderPlus, Layers } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Category } from '@/types/taxonomy';
import { categoryApi } from '@/services/categoryApi';
import { CategoryDrawer } from './CategoryDrawer';
import toast from 'react-hot-toast';

interface SortableRowProps {
  category: Category;
  isMaster: boolean;
  childCount?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onToggleStatus: (id: string, newStatus: 'active' | 'inactive') => void;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  onAddChild?: (parentId: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  category,
  isMaster,
  childCount = 0,
  isExpanded,
  onToggleExpand,
  onToggleStatus,
  onEdit,
  onDelete,
  onAddChild,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isActive = category.status === 'active';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3.5 py-3 transition-colors ${
        isDragging
          ? 'bg-indigo-50 dark:bg-slate-800 shadow-xl ring-2 ring-indigo-500 rounded-xl z-50 relative'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
      } ${!isMaster ? 'pl-10 sm:pl-12' : ''}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 transition flex-shrink-0 touch-none p-1"
        title="Sürükle Sırala"
      >
        <GripVertical size={18} />
      </button>

      {/* Expand/Collapse (sadece master) */}
      {isMaster ? (
        <button
          onClick={onToggleExpand}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
        >
          {childCount > 0 ? (
            isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />
          ) : (
            <span className="w-4" />
          )}
        </button>
      ) : (
        <span className="text-slate-400 dark:text-slate-600 flex-shrink-0 text-xs font-mono">↳</span>
      )}

      {/* Kategori Görseli */}
      {category.image ? (
        <img
          src={category.image}
          alt={category.name}
          className="w-9 h-9 rounded-xl object-cover flex-shrink-0 border border-slate-200 dark:border-slate-700 shadow-xs"
        />
      ) : (
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 border shadow-xs ${
          isMaster
            ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border-indigo-200/50 dark:border-indigo-800/50'
            : 'bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border-amber-200/50 dark:border-amber-800/50'
        }`}>
          {isMaster ? '📦' : '📂'}
        </div>
      )}

      {/* Kategori Bilgisi */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <span className={`font-semibold text-sm truncate ${
            isActive
              ? 'text-slate-900 dark:text-slate-100'
              : 'text-slate-400 dark:text-slate-500 line-through'
          }`}>
            {category.name}
          </span>
          {isMaster && childCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/50 dark:border-indigo-800/50">
              {childCount} alt
            </span>
          )}
          <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono flex-shrink-0 hidden sm:inline">
            /{category.slug}
          </span>
        </div>
      </div>

      {/* Status Toggle */}
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
        <input
          type="checkbox"
          checked={isActive}
          onChange={() => onToggleStatus(category.id, isActive ? 'inactive' : 'active')}
          className="sr-only peer"
        />
        <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-emerald-500"></div>
      </label>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0 ml-1">
        {isMaster && (
          <button
            onClick={() => onAddChild?.(category.id)}
            className="flex items-center justify-center min-w-[34px] min-h-[34px] text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded-xl transition-colors"
            title="Alt Kategori Ekle"
          >
            <FolderPlus size={16} />
          </button>
        )}
        <button
          onClick={() => onEdit(category)}
          className="flex items-center justify-center min-w-[34px] min-h-[34px] text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-xl transition-colors"
          title="Düzenle"
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onDelete(category.id)}
          className="flex items-center justify-center min-w-[34px] min-h-[34px] text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl transition-colors"
          title="Sil"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

interface SortableChildrenProps {
  children: Category[];
  onToggleStatus: (id: string, newStatus: 'active' | 'inactive') => void;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  onReorder: (parentId: string, oldIndex: number, newIndex: number) => void;
  parentId: string;
}

const SortableChildren: React.FC<SortableChildrenProps> = ({
  children,
  onToggleStatus,
  onEdit,
  onDelete,
  onReorder,
  parentId,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = children.findIndex((c) => c.id === active.id);
      const newIndex = children.findIndex((c) => c.id === over.id);
      onReorder(parentId, oldIndex, newIndex);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div className="bg-slate-50/60 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800/80 divide-y divide-slate-100/60 dark:divide-slate-800/40">
          {children.map((child) => (
            <SortableRow
              key={child.id}
              category={child}
              isMaster={false}
              onToggleStatus={onToggleStatus}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const Taxonomy: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [drawerParentId, setDrawerParentId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getCategories();
      setCategories(data.sort((a, b) => a.order - b.order));
      const newExpanded: Record<string, boolean> = {};
      data.filter((c) => !c.parentId).forEach((c) => {
        newExpanded[c.id] = expanded[c.id] !== undefined ? expanded[c.id] : true;
      });
      setExpanded(newExpanded);
    } catch (error: any) {
      toast.error(error.message || 'Kategoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const masterCategories = useMemo(
    () => categories.filter((c) => !c.parentId).sort((a, b) => a.order - b.order),
    [categories]
  );

  const getChildCategories = (parentId: string) =>
    categories.filter((c) => c.parentId === parentId).sort((a, b) => a.order - b.order);

  const filteredMasters = useMemo(() => {
    if (!searchQuery.trim()) return masterCategories;
    const q = searchQuery.toLowerCase();
    return masterCategories.filter((master) => {
      const masterMatch = master.name.toLowerCase().includes(q) || master.slug.toLowerCase().includes(q);
      const childMatch = getChildCategories(master.id).some(
        (child) => child.name.toLowerCase().includes(q) || child.slug.toLowerCase().includes(q)
      );
      return masterMatch || childMatch;
    });
  }, [masterCategories, searchQuery, categories]);

  const handleToggleStatus = async (id: string, newStatus: 'active' | 'inactive') => {
    setCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status: newStatus } : c))
    );

    try {
      await categoryApi.updateCategory(id, { status: newStatus });
    } catch (error: any) {
      setCategories((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, status: newStatus === 'active' ? 'inactive' : 'active' } : c
        )
      );
      toast.error(error.message || 'Durum güncellenemedi');
    }
  };

  const handleMasterDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = masterCategories.findIndex((c) => c.id === active.id);
    const newIndex = masterCategories.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(masterCategories, oldIndex, newIndex);

    const updates = reordered.map((cat, i) => ({ ...cat, order: i }));
    setCategories((prev) => {
      const childCategories = prev.filter((c) => c.parentId);
      return [...updates, ...childCategories].sort((a, b) => a.order - b.order);
    });

    try {
      await Promise.all(
        updates.map((cat) => categoryApi.updateCategory(cat.id, { order: cat.order }))
      );
    } catch (error: any) {
      toast.error('Sıralama kaydedilemedi');
      loadCategories();
    }
  };

  const handleChildReorder = async (parentId: string, oldIndex: number, newIndex: number) => {
    const children = getChildCategories(parentId);
    const reordered = arrayMove(children, oldIndex, newIndex);
    const updates = reordered.map((cat, i) => ({ ...cat, order: i }));

    setCategories((prev) => {
      const otherCats = prev.filter((c) => c.parentId !== parentId);
      return [...otherCats, ...updates].sort((a, b) => a.order - b.order);
    });

    try {
      await Promise.all(
        updates.map((cat) => categoryApi.updateCategory(cat.id, { order: cat.order }))
      );
    } catch (error: any) {
      toast.error('Sıralama kaydedilemedi');
      loadCategories();
    }
  };

  const handleDelete = async (id: string) => {
    const cat = categories.find((c) => c.id === id);
    const childCount = getChildCategories(id).length;
    const confirmMsg = childCount > 0
      ? `"${cat?.name}" kategorisi ve ${childCount} alt kategorisi silinecek. Emin misiniz?`
      : `"${cat?.name}" kategorisini silmek istediğinize emin misiniz?`;

    if (window.confirm(confirmMsg)) {
      try {
        await categoryApi.deleteCategory(id);
        toast.success('Kategori silindi');
        loadCategories();
      } catch (error: any) {
        toast.error(error.message || 'Kategori silinemedi');
      }
    }
  };

  const openDrawerForNew = () => {
    setEditingCategory(null);
    setDrawerParentId(null);
    setDrawerOpen(true);
  };

  const openDrawerForNewChild = (parentId: string) => {
    setEditingCategory(null);
    setDrawerParentId(parentId);
    setDrawerOpen(true);
  };

  const openDrawerForEdit = (category: Category) => {
    setEditingCategory(category);
    setDrawerParentId(null);
    setDrawerOpen(true);
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totalActive = categories.filter((c) => c.status === 'active').length;
  const totalInactive = categories.filter((c) => c.status === 'inactive').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
              <Layers size={20} />
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
              Kategori Yönetimi
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            {masterCategories.length} Ana Kategori • {categories.length - masterCategories.length} Alt Kategori
            <span className="mx-2">·</span>
            <span className="text-emerald-600 font-semibold">{totalActive} Aktif</span>
            {totalInactive > 0 && (
              <span className="text-rose-500 font-semibold ml-1.5">• {totalInactive} Pasif</span>
            )}
          </p>
        </div>
        <button
          onClick={openDrawerForNew}
          className="btn btn-primary self-start sm:self-auto shadow-indigo-500/20"
        >
          <Plus size={18} />
          Yeni Kategori Ekle
        </button>
      </div>

      {/* Arama Barı */}
      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Kategori adı veya slug ile ara..."
          className="input-field pl-10 pr-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          >
            ✕
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-9 w-9 border-3 border-indigo-600 border-t-transparent" />
        </div>
      )}

      {/* Kategori Tree Listesi */}
      {!loading && filteredMasters.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMasterDragEnd}>
          <SortableContext
            items={filteredMasters.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2.5">
              {filteredMasters.map((master) => {
                const children = getChildCategories(master.id);
                const isExp = expanded[master.id] !== false;

                return (
                  <div
                    key={master.id}
                    className="card !p-0 overflow-hidden border border-slate-200/80 dark:border-slate-800/80 divide-y divide-slate-100 dark:divide-slate-800/80"
                  >
                    <SortableRow
                      category={master}
                      isMaster={true}
                      childCount={children.length}
                      isExpanded={isExp}
                      onToggleExpand={() => toggleExpanded(master.id)}
                      onToggleStatus={handleToggleStatus}
                      onEdit={openDrawerForEdit}
                      onDelete={handleDelete}
                      onAddChild={openDrawerForNewChild}
                    />

                    {isExp && children.length > 0 && (
                      <SortableChildren
                        children={children}
                        onToggleStatus={handleToggleStatus}
                        onEdit={openDrawerForEdit}
                        onDelete={handleDelete}
                        onReorder={handleChildReorder}
                        parentId={master.id}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Boş Durum (Empty State) */}
      {!loading && filteredMasters.length === 0 && (
        <div className="card text-center py-16 px-4 border border-dashed border-slate-300 dark:border-slate-800">
          {searchQuery ? (
            <>
              <p className="text-slate-600 dark:text-slate-400 font-semibold text-base">
                "{searchQuery}" aramasına uygun kategori bulunamadı
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="btn btn-secondary btn-sm mt-3"
              >
                Aramayı Temizle
              </button>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto text-2xl mb-3">
                📦
              </div>
              <p className="text-slate-800 dark:text-slate-200 font-bold text-lg">Henüz Kategori Eklenmedi</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 max-w-sm mx-auto">
                Ürünlerinizi gruplandırmak için yeni bir ana kategori veya alt kategori oluşturabilirsiniz.
              </p>
              <button
                onClick={openDrawerForNew}
                className="btn btn-primary mt-4"
              >
                <Plus size={18} />
                İlk Kategoriyi Ekle
              </button>
            </>
          )}
        </div>
      )}

      {/* Category Drawer */}
      <CategoryDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingCategory(null);
          setDrawerParentId(null);
        }}
        onSaved={loadCategories}
        editingCategory={editingCategory}
        parentId={drawerParentId}
        masterCategories={masterCategories}
      />
    </div>
  );
};
